import { Action, ActionList } from "@dqm/queue-actions";
import { EnqueueActionsController } from "@dqm/react-enqueue-actions-component";
import { ValueService, ValueStatus } from "@dqm/value-observer";
import React, { useState } from "react";
import { PrintStatus } from "../../constants/PrintStatus";
import { EnqueueService } from "../../services/enqueue";
import { PrintingService } from "../../services/print";
import { ActionButton } from "./ActionButton";
import { UiStatusService } from "../../services/uiStatus/UiStatusService";
import { useUiStatus } from "../../services/uiStatus/UiStatusContext";
import { UiStatus } from "../../services/uiStatus/types/UiStatus";

export interface Props {
  valueService: ValueService;
  enqueueService: EnqueueService;
  printingService: PrintingService;
  onPrinterStatusChange?: (status: PrintStatus) => void;
}

const EnqueueActions: React.FC<Props> = ({
  valueService,
  enqueueService,
  printingService,
  onPrinterStatusChange,
}) => {
  const uiStatusService = useUiStatus() as UiStatusService;

  const [loading, setLoading] = useState(false);

  const setPrintingStatus = (status: PrintStatus) => {
    if (onPrinterStatusChange) {
      onPrinterStatusChange(status);
    }
    setLoading(status === PrintStatus.PRINTING);
  };

  const enqueue = async (payload: {}): Promise<void> => {
    setPrintingStatus(PrintStatus.PRINTING);
    uiStatusService.setStatus(UiStatus.PRINTING);

    try {
      const qn = await enqueueService.enqueue(payload);
      await printingService.print(qn);

      setPrintingStatus(PrintStatus.NONE);
      uiStatusService.setStatus(UiStatus.IDLE);
    } catch (err) {
      setPrintingStatus(PrintStatus.ERROR);
      uiStatusService.setStatus(UiStatus.PRINTER_ERROR);
    }
  };

  return (
    <div className="dqm-tcui-enqueue_actions">
      <EnqueueActionsController valueService={valueService}>
        {({
          actions,
          status,
        }: {
          actions: ActionList;
          status: ValueStatus;
        }) => {
          if (status === ValueStatus.Fault) {
            return null;
          }

          return actions.map((action) => (
            <ActionButton
              key={"enqueue-action-" + action.key}
              title={action.text}
              text={getActionDescription(action.key)}
              disabledText={getDisabedText(action.key)}
              isActive={isActionActive(action)}
              isLoading={loading}
              onAction={() => {
                enqueue(action.payload);
              }}
            />
          ));
        }}
      </EnqueueActionsController>
    </div>
  );
};

const isActionActive = (action: Action): boolean => {
  if ("active" in action) {
    return typeof action.active === "boolean" ? action.active : false;
  }
  return true;
};

const getActionDescription = (key: string): string => {
  switch (key) {
    case "yksityisasiakas":
      return "Privatkund - Private customer";
    case "yritysasiakas":
      return "Företagskund - Corporate customer";
    case "nettitilauksen-nouto":
      return "Avhämtning av weborder - Online order pickup";
    case "helppi":
      return "Teknisk support - Technical support";
    default:
      return "";
  }
};

const getDisabedText = (key: string): string => {
  switch (key) {
    case "yksityisasiakas":
    case "yritysasiakas":
    case "nouto":
      return "Jono on poissa käytöstä";
    case "helppi":
      return "AVAAMME KLO 10.00 Ma, ke ja pe";
    default:
      return "";
  }
};

export default EnqueueActions;
